// Work kit mutation seam (wave 4) — the F4 optimistic-mutation helper.
//
// PARALLEL-WAVE SEAM: per the W0 disjoint-ownership rule (one owner per
// file), W4 owns this minimal seam until the shared F4/W2 helper lands; W2
// (board) ADOPTS it, never forks it. Reconcile at merge.
//
// The kit performs no HTTP (W0 §5 — events are the only mutation API): a
// controller applies the change optimistically, EMITS the `ui--work:*` event,
// and the host POSTs from its glue island. The host then dispatches the
// matching `*-ack` consumption event; `adoptOrRollback` routes the ack.
//
// Toasts render through the EXISTING display path — nodes shaped exactly like
// `Ui::ToastComponent` markup and mounted with `data-controller="ui--toast"`
// (so the registered toast controller drives auto-dismiss/Escape), placed in
// the region the issue body renders. Parity details (fresh-eyes minor): the
// component's defaults are duration 5000 + dismissible, and `:loading`
// persists (duration 0) — the seam mirrors all three: duration 5000 and the
// close button on non-loading toasts, duration 0 without one on loading.
// Classes stay logical (P4): pe-8/end-2 where the legacy component still
// ships pr-8/right-2. The seam enforces the pinned
// exactly-one-toast rule: each region shows ONE toast at a time — a new toast
// replaces the current one, so two rapid error acks never stack two nodes.
//
// Server strings are diagnostics, never rendered (CONEK-6590 rule): callers
// pass kit i18n copy; ack error strings are ignored for display.

const TOAST_BASE =
  "relative flex items-start w-full gap-3 rounded-md border border-border bg-card p-4 text-sm text-foreground-medium shadow-lg pointer-events-auto"

// Icon tints mirror Ui::ToastComponent::VARIANTS exactly (danger for errors,
// green-medium for success, primary-500 for info/loading).
const TOAST_VARIANTS = {
  loading: { border: "", icon_color: "text-primary-500", icon: "spinner" },
  error: { border: "border-l-4 border-l-danger", icon_color: "text-danger", icon: "circle_x" },
  success: { border: "border-l-4 border-l-green-medium", icon_color: "text-green-medium", icon: "circle_check" },
  info: { border: "border-l-4 border-l-primary-500", icon_color: "text-primary-500", icon: "info" },
}

const SPINNER_SVG_NS = "http://www.w3.org/2000/svg"

// Stroke-glyph geometry for the named icons (same lucide paths the icon
// helper renders for Ui::ToastComponent) — drawn via createElementNS because
// the kit never uses innerHTML. `data-work-toast-icon` names the glyph for
// tests/telemetry.
const ICON_GLYPHS = {
  circle_x: [
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "10" } },
    { tag: "path", attrs: { d: "m15 9-6 6" } },
    { tag: "path", attrs: { d: "m9 9 6 6" } },
  ],
  circle_check: [
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "10" } },
    { tag: "path", attrs: { d: "m9 12 2 2 4-4" } },
  ],
  info: [
    { tag: "circle", attrs: { cx: "12", cy: "12", r: "10" } },
    { tag: "path", attrs: { d: "M12 16v-4" } },
    { tag: "path", attrs: { d: "M12 8h.01" } },
  ],
  x: [
    { tag: "path", attrs: { d: "M18 6 6 18" } },
    { tag: "path", attrs: { d: "m6 6 12 12" } },
  ],
}

// DOM-built twin of the `:loading` spinner in Ui::ToastComponent — no
// innerHTML anywhere in the seam.
function spinnerIcon() {
  const svg = document.createElementNS(SPINNER_SVG_NS, "svg")
  svg.setAttribute("class", "h-5 w-5 animate-spin")
  svg.setAttribute("viewBox", "0 0 24 24")
  svg.setAttribute("fill", "none")

  const circle = document.createElementNS(SPINNER_SVG_NS, "circle")
  circle.setAttribute("class", "opacity-25")
  circle.setAttribute("cx", "12")
  circle.setAttribute("cy", "12")
  circle.setAttribute("r", "10")
  circle.setAttribute("stroke", "currentColor")
  circle.setAttribute("stroke-width", "4")

  const path = document.createElementNS(SPINNER_SVG_NS, "path")
  path.setAttribute("class", "opacity-75")
  path.setAttribute("fill", "currentColor")
  path.setAttribute("d", "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z")

  svg.appendChild(circle)
  svg.appendChild(path)
  return svg
}

// DOM-built twin of the named icon in Ui::ToastComponent (`helpers.icon`).
function staticIcon(name, size = "h-5 w-5") {
  const svg = document.createElementNS(SPINNER_SVG_NS, "svg")
  svg.setAttribute("class", size)
  svg.setAttribute("viewBox", "0 0 24 24")
  svg.setAttribute("fill", "none")
  svg.setAttribute("stroke", "currentColor")
  svg.setAttribute("stroke-width", "2")
  svg.setAttribute("stroke-linecap", "round")
  svg.setAttribute("stroke-linejoin", "round")
  svg.setAttribute("data-work-toast-icon", name)
  ;(ICON_GLYPHS[name] || []).forEach((part) => {
    const el = document.createElementNS(SPINNER_SVG_NS, part.tag)
    Object.keys(part.attrs).forEach((key) => el.setAttribute(key, part.attrs[key]))
    svg.appendChild(el)
  })
  return svg
}

// Escape HTML metacharacters for toast text (textContent is used for the
// message itself; this guards nothing user-controlled today, kept explicit).
// The component's initializer rule: a default-duration `:loading` toast
// persists (0) until replaced; every other variant auto-dismisses at 5000ms.
const TOAST_DURATION_MS = 5000

function toastNode(message, variant) {
  const loading = variant === "loading"
  const node = document.createElement("div")
  node.className = `${TOAST_BASE} ${TOAST_VARIANTS[variant].border}${loading ? "" : " pe-8"}`.trim()
  node.setAttribute("data-controller", "ui--toast")
  node.setAttribute("data-ui--toast-duration-value", loading ? "0" : String(TOAST_DURATION_MS))
  node.setAttribute("data-work-toast-variant", variant)
  node.setAttribute("role", variant === "error" ? "alert" : "status")
  node.setAttribute("aria-live", variant === "error" ? "assertive" : "polite")

  const iconWrap = document.createElement("div")
  iconWrap.className = `shrink-0 ${TOAST_VARIANTS[variant].icon_color}`
  if (TOAST_VARIANTS[variant].icon === "spinner") {
    iconWrap.appendChild(spinnerIcon())
  } else {
    iconWrap.appendChild(staticIcon(TOAST_VARIANTS[variant].icon))
  }

  const content = document.createElement("div")
  content.className = "min-w-0 flex-1"
  const title = document.createElement("div")
  title.className = "text-sm font-semibold text-foreground"
  title.textContent = message
  content.appendChild(title)

  node.appendChild(iconWrap)
  node.appendChild(content)

  // Close button (the component's dismissible default) — the real ui--toast
  // controller owns the dismiss action, same as on component-rendered nodes.
  if (!loading) {
    const close = document.createElement("button")
    close.setAttribute("type", "button")
    close.setAttribute(
      "class",
      "absolute end-2 top-2 flex h-6 w-6 items-center justify-center rounded-md text-foreground-pale transition-colors hover:text-foreground-medium focus-visible:outline-none",
    )
    close.setAttribute("data-action", "click->ui--toast#dismiss")
    close.setAttribute("aria-label", "Close")
    close.appendChild(staticIcon("x", "h-4 w-4"))
    node.appendChild(close)
  }

  return node
}

// Exactly-one toast per region: replace whatever is showing. A loading toast
// persists (duration 0) until the ack replaces it — the `:loading` variant of
// Ui::ToastComponent persists the same way.
export function showToast(region, message, variant = "error") {
  if (!region) return null
  region.querySelectorAll("[data-work-toast]").forEach((node) => node.remove())
  const node = toastNode(message, variant)
  node.setAttribute("data-work-toast", "")
  region.appendChild(node)
  return node
}

export function clearToasts(region) {
  if (!region) return
  region.querySelectorAll("[data-work-toast]").forEach((node) => node.remove())
}

// Find the toast region a controller element belongs to. The issue body root
// renders one region; every work controller resolves it the same way.
export function toastRegionFor(element) {
  const root = element.closest("[data-controller~='ui--work-issue']")
  if (!root) return null
  const region = root.querySelector("[data-ui--work-issue-target='toastRegion']")
  return region || null
}

// Emit a `ui--work:*` mutation event from the issue BODY ROOT (bubbling) —
// hosts listen at the body root or at document level; the kit never POSTs.
export function emitFromIssueRoot(element, eventName, detail) {
  const root = element.closest("[data-controller~='ui--work-issue']") || element
  return root.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true }))
}

// Route an ack consumption event: `ok` adopts the server truth, `!ok` reverts
// and toasts exactly once. Returns "adopted" | "reverted" | "ignored".
export function adoptOrRollback({ ack, region, applyAdopt, applyRevert, errorMessage }) {
  if (!ack || typeof ack.ok !== "boolean") return "ignored"
  if (ack.ok) {
    applyAdopt?.(ack)
    clearToasts(region)
    return "adopted"
  }
  applyRevert?.(ack)
  // The English literal is a LAST-RESORT diagnostic for a body rendered
  // without the kit's `data-*-error` i18n attribute — every kit-rendered
  // body carries translated copy (issue_component.html.erb), so hosts see
  // localized text. Same rule for the fallbacks in issue_controller.
  showToast(region, errorMessage || "Changes could not be saved", "error")
  return "reverted"
}
